import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as CapLedgerService from '@/services/capLedgerService';

interface CompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  editingCompany?: CapLedgerService.Company | null;
}

export function CompanyDialog({
  open,
  onOpenChange,
  onSuccess,
  editingCompany,
}: CompanyDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    registration_number: '',
    incorporation_date: '',
    jurisdiction: '',
  });

  useEffect(() => {
    if (editingCompany) {
      setFormData({
        name: editingCompany.name || '',
        registration_number: editingCompany.registration_number || '',
        incorporation_date: editingCompany.incorporation_date || '',
        jurisdiction: editingCompany.jurisdiction || '',
      });
    } else {
      setFormData({ name: '', registration_number: '', incorporation_date: '', jurisdiction: '' });
    }
  }, [editingCompany, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCompany) {
        // Update existing company
        await CapLedgerService.updateCompany(editingCompany.id, {
          name: formData.name,
          registration_number: formData.registration_number || null,
          incorporation_date: formData.incorporation_date || null,
          jurisdiction: formData.jurisdiction || null,
        });
        toast.success('Company updated successfully');
      } else {
        // Create new company
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { error } = await supabase.from('companies').insert([
          {
            name: formData.name,
            registration_number: formData.registration_number || null,
            incorporation_date: formData.incorporation_date || null,
            jurisdiction: formData.jurisdiction || null,
            created_by: user.id,
          },
        ]);

        if (error) throw error;
        toast.success('Company created successfully');
      }

      onSuccess();
      onOpenChange(false);
      setFormData({ name: '', registration_number: '', incorporation_date: '', jurisdiction: '' });
    } catch (error: any) {
      toast.error(error.message || `Failed to ${editingCompany ? 'update' : 'create'} company`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle>
            <DialogDescription>
              {editingCompany ? 'Update company details' : 'Create a new company in your registry'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration_number">Registration Number</Label>
              <Input
                id="registration_number"
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="incorporation_date">Incorporation Date</Label>
              <Input
                id="incorporation_date"
                type="date"
                value={formData.incorporation_date}
                onChange={(e) => setFormData({ ...formData, incorporation_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="jurisdiction">Jurisdiction</Label>
              <Input
                id="jurisdiction"
                value={formData.jurisdiction}
                onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? editingCompany
                  ? 'Updating...'
                  : 'Creating...'
                : editingCompany
                  ? 'Update Company'
                  : 'Create Company'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
